import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { parse, getValidInboxSort } from '../../util/urlHelpers';
import { parseOrderNumberInput } from '../../util/orderNumber';
import { inboxParticipantUserFields } from '../../util/avatarImageQueryFields';
import { LISTING_CARD_MEDIA_FIELDS } from '../../util/listingMedia';
import {
  getAllTransitionsForEveryProcess,
  getSupportedProcessesInfo,
} from '../../transactions/transaction';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getSellerPrivateNote } from '../../util/sellerPrivateNote';

const INBOX_PAGE_SIZE = 12;

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

export const onlyFilterValues = {
  purchases: 'order',
  sales: 'sale',
};

// Terminal last transition for a checkout the buyer never confirmed.
// Sales omit this so abandoned payments do not appear in the seller inbox.
// Every other transition stays, including cancellations and booking expiry.
const PAYMENT_EXPIRED_LAST_TRANSITION = 'transition/expire-payment';

/**
 * Last transitions to request for the sales tab.
 * The query has no "exclude" filter, so this is every supported transition
 * except payment expiry. Purchases stay unfiltered.
 * @returns {string[]}
 */
export const lastTransitionsForSalesInbox = () => {
  return [...new Set(getAllTransitionsForEveryProcess())].filter(
    transition => transition !== PAYMENT_EXPIRED_LAST_TRANSITION
  );
};

/**
 * Build listingId → note map from ownListings query response data.
 * @param {Array} ownListingResources
 * @returns {Object<string, string>}
 */
export const notesByListingIdFromOwnListings = ownListingResources => {
  return (ownListingResources || []).reduce((acc, listing) => {
    const note = getSellerPrivateNote({ attributes: listing.attributes });
    if (note && listing?.id?.uuid) {
      acc[listing.id.uuid] = note;
    }
    return acc;
  }, {});
};

/**
 * Collect unique listing UUID strings from a transactions query response.
 * @param {Object} response - SDK transactions.query response
 * @returns {string[]}
 */
export const listingIdsFromTransactionsResponse = response => {
  const txs = response?.data?.data || [];
  const ids = txs.map(tx => tx.relationships?.listing?.data?.id?.uuid).filter(Boolean);
  return [...new Set(ids)];
};

// ================ Slice ================ //

const inboxPageSlice = createSlice({
  name: 'InboxPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
    sellerPrivateNotesByListingId: {},
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
        state.sellerPrivateNotesByListingId = {};
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const transactions = action.payload.data.data;
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(transactions);
        state.pagination = action.payload.data.meta;
        state.sellerPrivateNotesByListingId = action.payload.sellerPrivateNotesByListingId || {};
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
        state.sellerPrivateNotesByListingId = {};
      });
  },
});

export default inboxPageSlice.reducer;

// ================ Load data ================ //

const fetchSellerPrivateNotesForSales = (sdk, listingIds) => {
  if (!listingIds.length) {
    return Promise.resolve({});
  }

  return sdk.ownListings
    .query({
      ids: listingIds,
      perPage: Math.min(listingIds.length, 100),
      'fields.listing': ['privateData'],
    })
    .then(ownRes => notesByListingIdFromOwnListings(ownRes?.data?.data))
    .catch(() => {
      // Notes are optional UX; don't fail the sales list if ownListings fails.
      return {};
    });
};

/**
 * Build transactions.query params for the Purchases / Sales inbox.
 * @param {Object} args
 * @param {string} args.tab - 'purchases' | 'sales'
 * @param {string} args.search - location search string
 * @param {Object} args.config - marketplace config
 * @returns {Object}
 */
export const buildInboxQuery = ({ tab, search, config }) => {
  const onlyFilter = onlyFilterValues[tab];
  const { page = 1, sort, orderNumber } = parse(search);
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  let metaOrderNumber;
  if (typeof orderNumber === 'number') {
    metaOrderNumber = orderNumber;
  } else if (typeof orderNumber === 'string') {
    const parsed = parseOrderNumberInput(orderNumber);
    if (typeof parsed === 'number') {
      metaOrderNumber = parsed;
    }
  }

  return {
    only: onlyFilter,
    processNames,
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
    ],
    'fields.listing': [
      'title',
      'availabilityPlan',
      'publicData.listingType',
      ...LISTING_CARD_MEDIA_FIELDS,
    ],
    'fields.user': inboxParticipantUserFields,
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',
      // Listing card images
      ...listingCardImageFields(variantPrefix),
    ],
    ...listingCardImageVariantParams(variantPrefix, aspectRatio),
    page,
    perPage: INBOX_PAGE_SIZE,
    ...getValidInboxSort(sort),
    ...(onlyFilter === 'sale' ? { lastTransitions: lastTransitionsForSalesInbox() } : {}),
    ...(typeof metaOrderNumber === 'number' ? { meta_orderNumber: metaOrderNumber } : {}),
  };
};

const loadDataPayloadCreator = (
  { params, search, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const { tab } = params;

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for TransactionsPage: ${tab}`));
  }

  const apiQueryParams = buildInboxQuery({ tab, search, config });

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));

      if (onlyFilter !== 'sale') {
        return { ...response, sellerPrivateNotesByListingId: {} };
      }

      const listingIds = listingIdsFromTransactionsResponse(response);
      return fetchSellerPrivateNotesForSales(sdk, listingIds).then(
        sellerPrivateNotesByListingId => ({
          ...response,
          sellerPrivateNotesByListingId,
        })
      );
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('InboxPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for the thunk
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return dispatch(loadDataThunk({ params, search, config }));
};
